import React from 'react'
import { Stack } from 'react-bootstrap'
import { OAuth2App, ShortOAuth2App } from '../../../../store/auth/auth'
import { useAppAccessState } from '../hooks'
import { DisableButton, EnableButton, ManageButton, RevokeMyAccessButton, UpgradeButton } from '../../../../components/Button/IntegrationAndOAuth2AppButtons'
import { OAuth2AppsModalType } from '../Modals/OAuth2AppsModalManager'

interface OAuth2AppsButtonManagerProps {
  oAuth2App: OAuth2App | ShortOAuth2App
  onRequestModal: (modal: OAuth2AppsModalType) => void
  hideManageButton?: boolean
  hideDisableButton?: boolean
  hideRevokeAccessButton?: boolean
}

export const OAuth2AppsButtonManager: React.FC<OAuth2AppsButtonManagerProps> = ({
  oAuth2App,
  onRequestModal,
  hideManageButton = false,
  hideDisableButton = false,
  hideRevokeAccessButton = false,
}) => {
  const { clientId, name } = oAuth2App

  const { currentIntegration, hasAccessToApp, isEnabled, hasPermissionUpdateScope, oauth2Enabled, appNotBlacklisted, isPending } = useAppAccessState({
    clientId,
  })

  const stackDirection = hideManageButton ? 'horizontal' : 'vertical'
  const pendingWidth = !hideManageButton ? '100%' : '15rem'

  const showRevoke = isEnabled && currentIntegration && hasAccessToApp && !hideRevokeAccessButton

  const mainButton = (() => {
    if (hasPermissionUpdateScope && (!oauth2Enabled || !appNotBlacklisted)) {
      return (
        <Stack direction={stackDirection} className='gap-2 align-items-center'>
          <div className='paragraph-medium zocks-text-gray-600'>
            Available on the <b>Ultimate Plan</b>
          </div>
          <UpgradeButton onClick={() => onRequestModal('upgrade')} />
        </Stack>
      )
    }

    if (!oauth2Enabled || !appNotBlacklisted) {
      return (
        <Stack direction='horizontal' gap={3}>
          <i className='bi bi-lock' />
          <div className='zocks-text-gray-600'>This app isn't available for you. Contact your admin to get access.</div>
        </Stack>
      )
    }

    if (isEnabled && (currentIntegration || !hasAccessToApp) && !hideManageButton) {
      return <ManageButton to={`/account/integration-settings/oauth2apps/${clientId}/about`} />
    }

    if (isEnabled && hasPermissionUpdateScope && !hideDisableButton) {
      return <DisableButton onClick={() => onRequestModal('disable')} />
    }

    if (isEnabled && currentIntegration) {
      return null
    }

    if (isEnabled && hasAccessToApp) {
      return (
        <Stack direction='horizontal' gap={3}>
          <i className='bi bi-info-circle' />
          <div className='zocks-text-gray-600'>Connect from {name} to get started.</div>
        </Stack>
      )
    }

    if (hasPermissionUpdateScope && !isEnabled) {
      return <EnableButton onClick={() => onRequestModal('enable')} />
    }

    return (
      <Stack direction='horizontal' gap={3}>
        <i className='bi bi-lock' />
        <div className='zocks-text-gray-600'>This app isn't available for you. Contact your admin to get access.</div>
      </Stack>
    )
  })()

  if (isPending) {
    return <div className='skeleton' style={{ width: pendingWidth, height: '2.5rem' }} />
  }

  if (showRevoke) {
    return (
      <Stack direction='horizontal' gap={3}>
        <RevokeMyAccessButton onClick={() => onRequestModal('revoke')} />

        {mainButton && <div className='revoke-access-border'>{mainButton}</div>}
      </Stack>
    )
  }
  return <>{mainButton}</>
}
